'use client';

import { env } from '@ria-client/env';
import { type IFrameComponent, iframeResizer } from 'iframe-resizer';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserSupportsIframeSrcdoc,
  fetchIframeSrcDoc,
  getSrcValue,
  shouldLoadIframeSrcDoc,
  shouldRenderIframe,
} from './base-embed-utils';

interface IFrameProps {
  scrolling: 'no';
  ref: (ref: IFrameComponent) => void;
  onLoad: () => void;
  src: string;
  srcDoc?: string;
}

export interface BaseEmbedProps {
  src: string;
  title: string;
}

export function BaseEmbed({ src, title }: BaseEmbedProps): JSX.Element {
  const [ready, setReady] = useState(false);
  const [iframeSrcDoc, setIframeSrcDoc] = useState('');
  const [srcDocFetchComplete, setSrcDocFetchComplete] = useState(false);
  const frameRef = useRef<IFrameComponent | null>(null);
  const visibleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setIframeVisible = useCallback(() => {
    if (ready) return;
    if (visibleTimeoutRef.current) {
      clearTimeout(visibleTimeoutRef.current);
      visibleTimeoutRef.current = null;
    }

    setReady(true);
  }, [ready]);

  useEffect(() => {
    const onInfogramResize = (e: MessageEvent) => {
      if (!frameRef.current) return false;
      if (frameRef.current.contentWindow !== e.source) return false;

      let data;
      try {
        data = JSON.parse(e.data);
      } catch {
        return false;
      }

      if (data.context !== 'iframe.resize' || !data.height) {
        return false;
      }
      frameRef.current.height = data.height;
    };
    visibleTimeoutRef.current = setTimeout(setIframeVisible, 3000);
    window.addEventListener('message', onInfogramResize);

    return () => {
      window.removeEventListener('message', onInfogramResize);
      if (visibleTimeoutRef.current) clearTimeout(visibleTimeoutRef.current);
    };
  }, [setIframeVisible]);

  useEffect(() => {
    const fetchIframe = async () => {
      const compiledSrc = getSrcValue(undefined, src);
      if (shouldLoadIframeSrcDoc(compiledSrc)) {
        if (browserSupportsIframeSrcdoc) {
          try {
            const srcDoc = await fetchIframeSrcDoc(compiledSrc);
            setIframeSrcDoc(srcDoc);
            setSrcDocFetchComplete(true);
          } catch (err) {
            if (env.NEXT_PUBLIC_DEPLOYABLE_ENV !== 'prod') {
              console.error('An error occurred in Embed: ', err);
            }
            setSrcDocFetchComplete(true);
          }
        } else {
          setSrcDocFetchComplete(true);
        }
      }
    };

    fetchIframe();

    return () => {
      if (!frameRef.current) return;
      frameRef.current.iFrameResizer.close();
    };
  }, [src]);

  const onIframeRef = (ref: IFrameComponent) => {
    if (!ref) return;
    frameRef.current = ref;
    const config = {
      checkOrigin: false,
      log: false,
      onResized: () => {
        setIframeVisible();
      },
    };
    iframeResizer(config, ref);
  };

  const compiledSrc = getSrcValue(undefined, src);
  const iframeProps: IFrameProps = {
    scrolling: 'no',
    ref: onIframeRef,
    onLoad: () => {
      setIframeVisible();
    },
    src: compiledSrc,
  };
  if (iframeSrcDoc) {
    iframeProps.srcDoc = iframeSrcDoc;
  }

  return (
    <div className="overflow-hidden">
      {shouldRenderIframe(compiledSrc, srcDocFetchComplete) ? (
        <iframe
          {...iframeProps}
          title={title}
          style={{
            display: ready ? 'block' : 'none',
            border: 'none',
            width: '1px',
            minWidth: '100%',
          }}
        />
      ) : null}
    </div>
  );
}
